/**
 * High-performance financial data cache for efficient date range filtering.
 */

import { calculateExpenseAtMonth, calculateRevenueAtMonth } from './scaling.utils';
import {
  calculateAssetMonthlyImpact,
  calculateAssetPurchaseCashFlow,
  resolveActiveAssetEvents,
  resolveActiveRevenues,
  resolveActiveExpenses,
  startOfMonthUTC,
  isOnOrAfterStartupStart,
  isStartupStartMonth,
  sumFundingEventsInMonth,
  sumLiquidAssetBookValue,
} from '@burnchart/financial';
import { safeDate, parseCalendarDate } from '../date';
import { calculateBurndownSnapshot } from './burndown.utils';
import {
  shouldExcludeAcquisitionExpenseFromBurn,
} from './acquisition-expense.utils';
import { 
  calculateMonthlyCustomerMetrics, 
  // createCustomerProjectionFinancialEntries,
  type CustomerProjectionData 
} from './customer-projection.utils';
import type { Revenue, Expense } from '../../types/startup.types';
import { CHART_COLORS } from '@/constants/chart-colors';

export interface EventData {
  id: string;
  date: Date;
  type: "funding" | "milestone" | "launch" | "asset" | "custom";
  priority: "high" | "medium" | "low";
  description: string;
  amount?: number;
  icon?: string;
  color?: string;
  monthlyAppreciationRate?: number;
  minimumValue?: number;
  endDate?: Date;
  lineItemGroupId?: string;
  segmentKind?: "initial" | "revision";
  revisesId?: string;
  revisionNote?: string;
  suppressPurchaseCashFlow?: boolean;
  capacityGroupId?: string;
  projectionId?: string;
  capacityIncrease?: number;
}

export interface ChartData {
  startDate: Date;
  endDate: Date;
  initialBalance: number;
  expenses: Expense[];
  revenues?: Revenue[];
  events: EventData[];
  // Customer projection data
  customerProjectionData?: CustomerProjectionData;
}

export interface MonthlyFinancialData {
  month: Date;
  monthKey: string; // Format: "2024-03" for efficient lookups
  revenue: number;
  expenses: number;
  funding: number;
  assetImpact: number;
  assetAcquisitions: number;
  liquidAssetValue: number;
  oneTimeExpenses: number;
  cumulativeCash: number;
  netCashFlow: number;
  // Customer projection metrics
  customerMetrics?: {
    totalCustomers: number;
    newCustomers: number;
    churnedCustomers: number;
    subscriptionRevenue: number;
    acquisitionCosts: number;
    averageRevenuePerCustomer: number;
  };
}

export interface RunwayProjectionPoint {
  month: Date;
  actualCase: number;
  conservativeCase: number;
  optimisticCase: number;
  suggestedFunding?: number;
  fundingAmount?: number;
  assetAcquisitionValue?: number;
}

export interface CashTransition {
  /** Interpolated calendar date when available cash crosses zero */
  date: Date;
  /** Month bucket where the crossing occurs (UTC month start) */
  monthStart: Date;
  balance: number;
  type: 'zero' | 'negative';
  /** End-of-month balance from the prior month */
  previousBalance: number;
}

export const ZERO_CASH_MARKER_COLOR = '#fbbf24';

function endOfMonthUTC(monthStart: Date): Date {
  const end = new Date(monthStart);
  end.setUTCMonth(end.getUTCMonth() + 1);
  end.setUTCDate(0);
  end.setUTCHours(23, 59, 59, 999);
  return end;
}

function daysInMonthUTC(monthStart: Date): number {
  return endOfMonthUTC(monthStart).getUTCDate();
}

/**
 * Linearly interpolates the day within a month when cash crosses from positive to zero/negative,
 * using end-of-month balances from the prior and current months.
 */
export function interpolateCashZeroDate(
  monthStart: Date,
  previousMonthBalance: number,
  currentMonthBalance: number,
): Date {
  if (currentMonthBalance > 0) {
    return new Date(monthStart);
  }

  if (previousMonthBalance <= 0) {
    return new Date(monthStart);
  }

  if (currentMonthBalance === 0) {
    return endOfMonthUTC(monthStart);
  }

  const daysInMonth = daysInMonthUTC(monthStart);
  const dayOfMonth = Math.min(
    daysInMonth,
    Math.max(
      1,
      Math.round(
        (daysInMonth * previousMonthBalance) /
          (previousMonthBalance - currentMonthBalance),
      ),
    ),
  );

  return new Date(
    Date.UTC(
      monthStart.getUTCFullYear(),
      monthStart.getUTCMonth(),
      dayOfMonth,
      12,
      0,
      0,
    ),
  );
}

export function formatCashZeroDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function computeCashTransitions(
  monthlyEntries: MonthlyFinancialData[],
): CashTransition[] {
  const sorted = [...monthlyEntries].sort(
    (a, b) => a.month.getTime() - b.month.getTime(),
  );
  const transitions: CashTransition[] = [];
  let wasPositive = false;
  let previousBalance = 0;

  for (const monthData of sorted) {
    const currentBalance = monthData.cumulativeCash;

    if (wasPositive && currentBalance <= 0) {
      transitions.push({
        date: interpolateCashZeroDate(
          monthData.month,
          previousBalance,
          currentBalance,
        ),
        monthStart: new Date(monthData.month),
        balance: currentBalance,
        type: currentBalance === 0 ? 'zero' : 'negative',
        previousBalance,
      });
      wasPositive = false;
    } else if (!wasPositive && currentBalance > 0) {
      wasPositive = true;
    }

    previousBalance = currentBalance;
  }

  return transitions;
}

function getOneTimeExpenseAmount(expense: Expense): number {
  return expense.oneTimeAmount ?? expense.monthlyAmount ?? expense.amount ?? 0;
}

function normalizeChartData(data: ChartData): ChartData {
  return {
    ...data,
    startDate: safeDate(data.startDate),
    endDate: safeDate(data.endDate),
    expenses: (data.expenses || []).map((expense) => ({
      ...expense,
      startDate: expense.startDate ? safeDate(expense.startDate) : expense.startDate,
      endDate: expense.endDate ? safeDate(expense.endDate) : expense.endDate,
    })),
    revenues: (data.revenues || []).map((revenue) => ({
      ...revenue,
      startDate: revenue.startDate ? safeDate(revenue.startDate) : revenue.startDate,
      endDate: revenue.endDate ? safeDate(revenue.endDate) : revenue.endDate,
      date: revenue.date ? safeDate(revenue.date) : revenue.date,
    })),
    events: (data.events || []).map((event) => ({
      ...event,
      date: safeDate(event.date),
    })),
    customerProjectionData: data.customerProjectionData
      ? {
          ...data.customerProjectionData,
          projections: (data.customerProjectionData.projections || []).map((projection) => ({
            ...projection,
            startDate: safeDate(projection.startDate as unknown),
            endDate: projection.endDate
              ? safeDate(projection.endDate as unknown)
              : projection.endDate,
          })),
          capacityEvents: (data.customerProjectionData.capacityEvents || []).map((event) => ({
            ...event,
            date: safeDate(event.date as unknown),
          })),
          growthScenarios: (data.customerProjectionData.growthScenarios || []).map(
            (scenario) => ({
              ...scenario,
              startDate: safeDate(scenario.startDate as unknown),
            }),
          ),
        }
      : undefined,
  };
}

function toBurndownChartData(
  data: ChartData,
): Parameters<typeof calculateBurndownSnapshot>[0] {
  return {
    startDate: data.startDate,
    endDate: data.endDate,
    initialBalance: data.initialBalance,
    expenses: (data.expenses || []).map((expense) => ({
      id: expense.id,
      name: expense.name,
      category: expense.category,
      monthlyAmount: expense.monthlyAmount ?? expense.amount,
      oneTimeAmount: expense.oneTimeAmount ?? (expense.isOneTime ? expense.amount : undefined),
      startDate: safeDate(expense.startDate!),
      endDate: expense.endDate ? safeDate(expense.endDate) : undefined,
      color: expense.color ?? CHART_COLORS.expense,
      expenseType: expense.expenseType,
      growthRate: expense.growthRate,
      revenueRatio: expense.revenueRatio,
      customerBaseCostPerCustomer: expense.customerBaseCostPerCustomer,
      customerBaseAmount: expense.customerBaseAmount,
      isOneTime: expense.isOneTime,
    })),
    revenues: (data.revenues || []).map((revenue) => ({
      id: revenue.id,
      name: revenue.name,
      category: (revenue.type ?? 'other') as 'funding' | 'sales' | 'investment' | 'grant' | 'other',
      monthlyAmount: revenue.monthlyAmount ?? revenue.amount ?? 0,
      startDate: safeDate(revenue.startDate ?? revenue.date ?? data.startDate),
      endDate: revenue.endDate ? safeDate(revenue.endDate) : undefined,
      color: '#10b981',
      isRecurring: revenue.isRecurring ?? false,
      frequency: revenue.frequency ?? 'monthly',
    })),
    events: data.events || [],
    customerProjectionData: data.customerProjectionData,
  };
}

function addCashCheckDate(
  timestamps: Set<number>,
  date: Date,
  rangeStart: Date,
  rangeEnd: Date,
  includePreviousDay = true,
): void {
  const time = date.getTime();
  if (time < rangeStart.getTime() || time > rangeEnd.getTime()) {
    return;
  }

  timestamps.add(time);

  if (includePreviousDay) {
    const previousDay = new Date(date);
    previousDay.setUTCDate(previousDay.getUTCDate() - 1);
    previousDay.setUTCHours(12, 0, 0, 0);
    if (
      previousDay.getTime() >= rangeStart.getTime() &&
      previousDay.getTime() <= rangeEnd.getTime()
    ) {
      timestamps.add(previousDay.getTime());
    }
  }
}

function collectCashCheckDates(
  data: ChartData,
  rangeStart: Date,
  rangeEnd: Date,
): Date[] {
  const timestamps = new Set<number>();

  addCashCheckDate(timestamps, rangeStart, rangeStart, rangeEnd, false);
  addCashCheckDate(timestamps, rangeEnd, rangeStart, rangeEnd, false);
  addCashCheckDate(timestamps, data.startDate, rangeStart, rangeEnd, true);

  let month = startOfMonthUTC(rangeStart);
  const rangeEndMonth = startOfMonthUTC(rangeEnd);

  while (month.getTime() <= rangeEndMonth.getTime()) {
    addCashCheckDate(timestamps, month, rangeStart, rangeEnd, false);
    addCashCheckDate(timestamps, endOfMonthUTC(month), rangeStart, rangeEnd, false);
    month.setUTCMonth(month.getUTCMonth() + 1);
  }

  for (const expense of data.expenses || []) {
    if (expense.isOneTime && expense.startDate) {
      addCashCheckDate(
        timestamps,
        safeDate(expense.startDate),
        rangeStart,
        rangeEnd,
        true,
      );
    }
  }

  for (const revenue of data.revenues || []) {
    if (revenue.frequency === 'one-time' && revenue.startDate) {
      addCashCheckDate(
        timestamps,
        safeDate(revenue.startDate),
        rangeStart,
        rangeEnd,
        true,
      );
    }
  }

  for (const event of data.events || []) {
    addCashCheckDate(timestamps, event.date, rangeStart, rangeEnd, true);
  }

  return Array.from(timestamps)
    .sort((a, b) => a - b)
    .map((time) => {
      const date = new Date(time);
      date.setUTCHours(12, 0, 0, 0);
      return date;
    });
}

export function interpolateZeroCrossingDate(
  prevDate: Date,
  prevBalance: number,
  nextDate: Date,
  nextBalance: number,
): Date {
  if (
    prevBalance <= 0 ||
    nextBalance > 0 ||
    prevDate.getTime() === nextDate.getTime()
  ) {
    return new Date(nextDate);
  }

  const ratio = prevBalance / (prevBalance - nextBalance);
  const ms =
    prevDate.getTime() + ratio * (nextDate.getTime() - prevDate.getTime());
  return new Date(ms);
}

/**
 * Detect zero crossings using the same burndown snapshot model as the timeline scrubber,
 * checking balances on month boundaries and one-time expense / event dates.
 */
export function computeCashTransitionsFromSnapshots(
  data: ChartData,
  rangeStart: Date,
  rangeEnd: Date,
): CashTransition[] {
  const burndownData = toBurndownChartData(data);
  const checkDates = collectCashCheckDates(data, rangeStart, rangeEnd);
  const transitions: CashTransition[] = [];
  let wasPositive = false;
  let prevDate: Date | null = null;
  let prevBalance = 0;

  for (const date of checkDates) {
    const balance = calculateBurndownSnapshot(burndownData, date).cashBalance;

    if (wasPositive && balance <= 0 && prevDate) {
      const zeroDate = interpolateZeroCrossingDate(
        prevDate,
        prevBalance,
        date,
        balance,
      );
      transitions.push({
        date: zeroDate,
        monthStart: startOfMonthUTC(zeroDate),
        balance,
        type: balance === 0 ? 'zero' : 'negative',
        previousBalance: prevBalance,
      });
      wasPositive = false;
    } else if (!wasPositive && balance > 0) {
      wasPositive = true;
    }

    prevDate = date;
    prevBalance = balance;
  }

  return transitions;
}

export interface FinancialDataCache {
  monthlyData: Map<string, MonthlyFinancialData>;
  cashTransitions: CashTransition[]; // Pre-computed transitions
  minDate: Date;
  maxDate: Date;
  lastUpdated: number;
  dataHash: string; // For cache invalidation
}

function hashDate(value?: unknown): string {
  const date = parseCalendarDate(value as Parameters<typeof parseCalendarDate>[0]);
  return date ? String(date.getTime()) : '';
}

/**
 * Creates a hash of the input data for cache invalidation
 */
function createDataHash(data: ChartData): string {
  const expenseHash = (data.expenses || [])
    .map(
      (e) =>
        `${e.id}-${e.isOneTime}-${e.oneTimeAmount ?? ''}-${e.monthlyAmount ?? e.amount ?? 0}-${e.expenseType || 'fixed'}-${e.growthRate || 0}-${e.revenueRatio || 0}-${e.customerBaseCostPerCustomer || 0}-${e.customerBaseAmount || 0}-${hashDate(e.startDate)}-${hashDate(e.endDate)}`,
    )
    .join('|');
  const revenueHash = (data.revenues || [])
    .map(
      (r) =>
        `${r.id}-${r.amount || r.monthlyAmount}-${r.growthRate || 0}-${r.frequency || 'monthly'}-${r.isCustomerDriven ? 1 : 0}-${hashDate(r.startDate)}-${hashDate(r.endDate)}`,
    )
    .join('|');
  const eventHash = (data.events || [])
    .map(
      (e) =>
        `${e.id}-${e.type}-${e.amount || 0}-${e.monthlyAppreciationRate ?? ''}-${e.capacityGroupId ?? ''}-${e.projectionId ?? ''}-${e.capacityIncrease ?? ''}-${hashDate(e.date)}`,
    )
    .join('|');
  const customerProjectionHash = data.customerProjectionData
    ? [
        ...(data.customerProjectionData.projections || []).map(
          (p) =>
            `${p.id}-${p.subscriptionTierId}-${p.projectionType}-${p.initialCustomers ?? 0}-${p.monthlyAcquisitionTarget ?? 0}-${p.monthlyGrowthRate ?? 0}-${p.churnRateMonthly ?? 0}-${p.acquisitionCostPerCustomer ?? 0}-${p.monthlyServicingCostPerCustomer ?? 0}-${p.maxCustomers ?? ''}-${p.maxMonthlyAcquisitions ?? ''}-${p.capacityGroupId ?? ''}-${hashDate(p.startDate as Date)}-${hashDate(p.endDate as Date | undefined)}`,
        ),
        ...(data.customerProjectionData.subscriptionTiers || []).map(
          (t) =>
            `${t.id}-${t.price}-${t.billingFrequency}-${t.revenueModel ?? 'subscription'}-${t.pricePerVisit ?? ''}-${t.avgVisitsPerCustomerPerMonth ?? ''}`,
        ),
        ...(data.customerProjectionData.acquisitionCostModels || []).map(
          (m) =>
            `${m.id}-${m.fixedCostPerCustomer ?? 0}-${m.acquisitionChannel ?? ''}`,
        ),
        ...(data.customerProjectionData.capacityGroups || []).map(
          (g) => `${g.id}-${g.maxCustomers}-${g.name}`,
        ),
        ...(data.customerProjectionData.capacityEvents || []).map(
          (ev) =>
            `${ev.capacityGroupId ?? ''}-${ev.projectionId ?? ''}-${ev.capacityIncrease ?? ''}-${hashDate(ev.date as Date)}`,
        ),
        ...(data.customerProjectionData.growthScenarios || []).map(
          (s) =>
            `${s.id}-${s.marketSaturationPoint ?? ''}-${s.saturationSlowdownRate ?? ''}-${hashDate(s.startDate as Date)}`,
        ),
      ].join('|')
    : '';

  return `${data.initialBalance || 0}-${hashDate(data.startDate)}-${hashDate(data.endDate)}-${expenseHash}-${revenueHash}-${eventHash}-${customerProjectionHash}`;
}

/**
 * Pre-computes all financial data for efficient date range filtering
 */
export function buildFinancialDataCache(data: ChartData): FinancialDataCache {
  const chartData = normalizeChartData(data);
  const monthlyData = new Map<string, MonthlyFinancialData>();
  
  // Calculate the full date range (extend beyond data bounds for flexibility)
  const now = new Date();
  const defaultStartDate = new Date(now.getFullYear() - 1, now.getMonth(), 1); // 1 year ago
  const defaultEndDate = new Date(now.getFullYear() + 2, now.getMonth(), 1); // 2 years from now
  
  const dataStartDate = new Date(Math.min(
    chartData.startDate ? chartData.startDate.getTime() : defaultStartDate.getTime(),
    ...((chartData.expenses || []).filter(e => e.startDate).map(e => safeDate(e.startDate!).getTime())),
    ...((chartData.revenues || []).filter(r => r.startDate).map(r => safeDate(r.startDate!).getTime())),
    ...((chartData.events || []).map(e => safeDate(e.date).getTime()))
  ));
  
  const dataEndDate = new Date(Math.max(
    chartData.endDate ? chartData.endDate.getTime() : defaultEndDate.getTime(),
    ...((chartData.expenses || []).filter(e => e.endDate).map(e => safeDate(e.endDate!).getTime())),
    ...((chartData.revenues || []).filter(r => r.endDate).map(r => safeDate(r.endDate!).getTime())),
    ...((chartData.events || []).map(e => safeDate(e.date).getTime()))
  ));
  
  // Extend range by 2 years on each side for zoom flexibility
  const minDate = new Date(dataStartDate.getFullYear() - 2, 0, 1);
  const maxDate = new Date(dataEndDate.getFullYear() + 2, 11, 31);
  
  let cumulativeCash = 0;
  let currentMonth = startOfMonthUTC(minDate);
  const maxMonth = startOfMonthUTC(maxDate);

  while (currentMonth.getTime() <= maxMonth.getTime()) {
    const monthKey = `${currentMonth.getUTCFullYear()}-${String(currentMonth.getUTCMonth() + 1).padStart(2, '0')}`;

    if (isStartupStartMonth(chartData.startDate, currentMonth)) {
      cumulativeCash += chartData.initialBalance || 0;
    }

    let customerMetrics: ReturnType<typeof calculateMonthlyCustomerMetrics> | undefined;
    let customerSubscriptionRevenue = 0;
    let customerAcquisitionCosts = 0;
    let totalMonthRevenue = 0;
    let monthExpenses = 0;
    let oneTimeRevenueTotal = 0;
    let oneTimeExpenseTotal = 0;
    let fundingAmount = 0;
    let assetImpact = 0;
    let assetPurchaseCash = 0;
    let netCashFlow = 0;

    if (chartData.customerProjectionData) {
      customerMetrics = calculateMonthlyCustomerMetrics(
        currentMonth,
        chartData.customerProjectionData,
      );
      customerSubscriptionRevenue = customerMetrics.subscriptionRevenue;
      customerAcquisitionCosts = customerMetrics.acquisitionCosts;
      customerAcquisitionCosts += customerMetrics.servicingCosts ?? 0;
    }

    if (isOnOrAfterStartupStart(chartData.startDate, currentMonth)) {
    const monthlyRevenues = resolveActiveRevenues(
      chartData.revenues || [],
      currentMonth,
    ).filter((revenue) => {
      if (revenue.frequency === "one-time") return false;
      if (chartData.customerProjectionData && revenue.isCustomerDriven) return false;
      return true;
    });
    
    const monthRevenue = monthlyRevenues.reduce((sum, revenue) => {
      const revenueForCalculation = {
        id: revenue.id,
        name: revenue.name,
        amount: revenue.amount || revenue.monthlyAmount || 0,
        monthlyAmount: revenue.monthlyAmount,
        type: (revenue.type ?? revenue.category) as any,
        date: safeDate(revenue.startDate),
        startupId: (revenue as any).startupId ?? "",
        startDate: revenue.startDate,
        endDate: revenue.endDate,
        isRecurring: revenue.isRecurring,
        frequency: revenue.frequency,
        growthRate: revenue.growthRate,
        isCustomerDriven: revenue.isCustomerDriven,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      return sum + calculateRevenueAtMonth(revenueForCalculation, currentMonth);
    }, 0) + customerSubscriptionRevenue;

    const oneTimeRevenuesThisMonth = (chartData.revenues || []).filter((revenue) => {
      if (revenue.frequency !== "one-time" || !revenue.startDate) return false;
      return (
        startOfMonthUTC(safeDate(revenue.startDate)).getTime() ===
        currentMonth.getTime()
      );
    });

    oneTimeRevenueTotal = oneTimeRevenuesThisMonth.reduce((sum, revenue) => {
      return sum + (revenue.amount || revenue.monthlyAmount || 0);
    }, 0);

    totalMonthRevenue = monthRevenue + oneTimeRevenueTotal;

    const monthlyExpenses = resolveActiveExpenses(
      chartData.expenses || [],
      currentMonth,
    ).filter(
      (expense) =>
        !shouldExcludeAcquisitionExpenseFromBurn(
          expense,
          chartData.customerProjectionData,
        ) && expense.startDate,
    );

    monthExpenses = monthlyExpenses.reduce((sum, expense) => {
      const expenseForCalculation: Expense = {
        id: expense.id,
        name: expense.name,
        // For customer-based expenses, don't pass the stored amount as it should be calculated dynamically
        amount: expense.expenseType === "customer_based" ? 0 : (expense.monthlyAmount || expense.amount || 0),
        frequency: "monthly" as const,
        category: expense.category,
        startDate: expense.startDate,
        endDate: expense.endDate,
        isRecurring: true,
        startupId: "",
        expenseType: expense.expenseType || "fixed",
        growthRate: expense.growthRate,
        revenueRatio: expense.revenueRatio,
        customerBaseCostPerCustomer: expense.customerBaseCostPerCustomer,
        customerBaseAmount: expense.customerBaseAmount,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      const calculatedAmount = calculateExpenseAtMonth(expenseForCalculation, currentMonth, monthRevenue, customerMetrics);
      return sum + calculatedAmount;
    }, 0) + customerAcquisitionCosts;

    const oneTimeExpensesThisMonth = (chartData.expenses || []).filter((expense) => {
      if (!expense.isOneTime || !expense.startDate) return false;
      if (getOneTimeExpenseAmount(expense) <= 0) return false;
      return (
        startOfMonthUTC(safeDate(expense.startDate)).getTime() ===
        currentMonth.getTime()
      );
    });

    oneTimeExpenseTotal = oneTimeExpensesThisMonth.reduce((sum, expense) => {
      return sum + getOneTimeExpenseAmount(expense);
    }, 0);

    assetImpact = resolveActiveAssetEvents(chartData.events || [], currentMonth).reduce(
      (sum, asset) => sum + calculateAssetMonthlyImpact(asset, currentMonth),
      0,
    );

    fundingAmount = sumFundingEventsInMonth(chartData.events, currentMonth);

    assetPurchaseCash = resolveActiveAssetEvents(chartData.events || [], currentMonth).reduce(
      (sum, event) => sum + calculateAssetPurchaseCashFlow(event, currentMonth),
      0,
    );

    const totalExpenses = monthExpenses + oneTimeExpenseTotal;
    netCashFlow = totalMonthRevenue + fundingAmount + assetPurchaseCash - totalExpenses;
    cumulativeCash += netCashFlow;
    }

    const liquidAssetValue = sumLiquidAssetBookValue(chartData.events || [], currentMonth);
    
    // Store monthly data
    monthlyData.set(monthKey, {
      month: new Date(currentMonth),
      monthKey,
      revenue: totalMonthRevenue,
      expenses: monthExpenses,
      funding: fundingAmount,
      assetImpact,
      assetAcquisitions: assetPurchaseCash,
      liquidAssetValue,
      oneTimeExpenses: oneTimeExpenseTotal,
      cumulativeCash: cumulativeCash + liquidAssetValue,
      netCashFlow,
      customerMetrics: customerMetrics ? {
        totalCustomers: customerMetrics.totalCustomers,
        newCustomers: customerMetrics.newCustomers,
        churnedCustomers: customerMetrics.churnedCustomers,
        subscriptionRevenue: customerMetrics.subscriptionRevenue,
        acquisitionCosts: customerMetrics.acquisitionCosts,
        averageRevenuePerCustomer: customerMetrics.averageRevenuePerCustomer,
      } : undefined,
    });
    
    currentMonth.setUTCMonth(currentMonth.getUTCMonth() + 1);
  }

  const cashTransitions = computeCashTransitionsFromSnapshots(
    chartData,
    minDate,
    maxDate,
  );
  
  return {
    monthlyData,
    cashTransitions,
    minDate,
    maxDate,
    lastUpdated: Date.now(),
    dataHash: createDataHash(data),
  };
}

/**
 * Efficiently extracts data for a specific date range from the cache
 */
export function getDateRangeData(
  cache: FinancialDataCache,
  startDate: Date,
  endDate: Date
): MonthlyFinancialData[] {
  const result: MonthlyFinancialData[] = [];
  let currentMonth = startOfMonthUTC(startDate);
  const rangeEndMonth = startOfMonthUTC(endDate);

  while (currentMonth.getTime() <= rangeEndMonth.getTime()) {
    const monthKey = `${currentMonth.getUTCFullYear()}-${String(currentMonth.getUTCMonth() + 1).padStart(2, '0')}`;
    const monthData = cache.monthlyData.get(monthKey);

    if (monthData) {
      result.push(monthData);
    }

    currentMonth.setUTCMonth(currentMonth.getUTCMonth() + 1);
  }

  return result;
}

/**
 * Build runway projection series using the same available-cash model as burndown.
 * All scenarios share the actual baseline at the first visible month, then follow
 * the same non-operational jumps as actual (initial balance, funding, assets) while
 * diverging only on revenue/expense scenario assumptions.
 */
export function buildRunwayProjectionPoints(
  rangeData: MonthlyFinancialData[],
): RunwayProjectionPoint[] {
  let conservativeCash = 0;
  let optimisticCash = 0;

  return rangeData.map((monthData, index) => {
    const actualCase = Math.round(monthData.cumulativeCash);

    const conservativeRevenue = monthData.revenue * 0.8;
    const conservativeExpenses =
      (monthData.expenses + monthData.oneTimeExpenses) * 1.1;
    const conservativeNet =
      conservativeRevenue +
      monthData.funding +
      monthData.assetAcquisitions -
      conservativeExpenses;

    const optimisticRevenue = monthData.revenue * 1.3;
    const optimisticExpenses =
      (monthData.expenses + monthData.oneTimeExpenses) * 0.95;
    const optimisticNet =
      optimisticRevenue +
      monthData.funding +
      monthData.assetAcquisitions -
      optimisticExpenses;

    if (index === 0) {
      conservativeCash = actualCase;
      optimisticCash = actualCase;
    } else {
      const prev = rangeData[index - 1];
      const actualDelta = monthData.cumulativeCash - prev.cumulativeCash;
      const operationalScenarioDelta =
        conservativeNet - monthData.netCashFlow;
      const optimisticOperationalDelta = optimisticNet - monthData.netCashFlow;

      conservativeCash += actualDelta + operationalScenarioDelta;
      optimisticCash += actualDelta + optimisticOperationalDelta;
    }

    const suggestedFunding =
      conservativeCash < 0 ? Math.abs(conservativeCash) + 50000 : undefined;

    return {
      month: monthData.month,
      actualCase,
      conservativeCase: Math.round(conservativeCash),
      optimisticCase: Math.round(optimisticCash),
      suggestedFunding: suggestedFunding
        ? Math.round(suggestedFunding)
        : undefined,
      fundingAmount:
        monthData.funding > 0 ? Math.round(monthData.funding) : undefined,
      assetAcquisitionValue:
        monthData.assetAcquisitions > 0
          ? Math.round(monthData.assetAcquisitions)
          : undefined,
    };
  });
}

/**
 * Gets cached cash transitions filtered by date range
 */
export function getCashTransitions(
  cache: FinancialDataCache,
  startDate: Date,
  endDate: Date
): CashTransition[] {
  return cache.cashTransitions.filter(transition => 
    transition.date >= startDate && transition.date <= endDate
  );
}

/** Primary zero-cash event for display: next upcoming, or most recent if already past zero */
export function getCashZeroEventForDisplay(
  cache: FinancialDataCache,
  currentDate: Date,
  currentCashBalance?: number,
): CashTransition | null {
  const events = cache.cashTransitions;
  if (events.length === 0) return null;

  const upcoming = events.find(
    (event) => event.date.getTime() > currentDate.getTime(),
  );
  if (upcoming) return upcoming;

  if (currentCashBalance !== undefined && currentCashBalance > 0) {
    return null;
  }

  const past = events.filter(
    (event) => event.date.getTime() <= currentDate.getTime(),
  );
  return past.length > 0 ? past[past.length - 1]! : null;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const AVERAGE_DAYS_PER_MONTH = 30.437;

function monthsUntil(from: Date, to: Date): number {
  return Math.max(0, (to.getTime() - from.getTime()) / MS_PER_DAY) / AVERAGE_DAYS_PER_MONTH;
}

/**
 * Runway in months until cash runs out. Uses the sooner of:
 * - cash / current monthly net burn (steady-state)
 * - projected months until the next zero-cash event (one-time expenses, etc.)
 */
export function computeRunwayMonths(
  cache: FinancialDataCache,
  currentDate: Date,
  cashBalance: number,
  netMonthlyChange: number,
): number {
  if (cashBalance <= 0) {
    return 0;
  }

  const upcomingZero = cache.cashTransitions.find(
    (event) => event.date.getTime() > currentDate.getTime(),
  );

  const burnRunway =
    netMonthlyChange >= 0
      ? Infinity
      : cashBalance / Math.abs(netMonthlyChange);

  const projectedRunway = upcomingZero
    ? monthsUntil(currentDate, upcomingZero.date)
    : Infinity;

  if (burnRunway === Infinity && projectedRunway === Infinity) {
    return Infinity;
  }
  if (burnRunway === Infinity) {
    return projectedRunway;
  }
  if (projectedRunway === Infinity) {
    return burnRunway;
  }

  return Math.min(burnRunway, projectedRunway);
}

/** Primary zero-cash marker in a visible range (prefers next upcoming crossing) */
export function getPrimaryCashZeroInRange(
  cache: FinancialDataCache,
  startDate: Date,
  endDate: Date,
  currentDate: Date,
  currentCashBalance?: number,
): CashTransition | null {
  const inRange = getCashTransitions(cache, startDate, endDate);
  if (inRange.length === 0) return null;

  const upcoming = inRange.find(
    (event) => event.date.getTime() >= currentDate.getTime(),
  );
  if (upcoming) return upcoming;

  if (currentCashBalance !== undefined && currentCashBalance > 0) {
    return null;
  }

  const past = inRange.filter(
    (event) => event.date.getTime() <= currentDate.getTime(),
  );
  return past.length > 0 ? past[past.length - 1]! : null;
}

/**
 * Checks if the cache is still valid for the given data
 */
export function isCacheValid(cache: FinancialDataCache | null, data: ChartData): boolean {
  if (!cache) return false;
  
  const currentHash = createDataHash(data);
  return cache.dataHash === currentHash;
}

/**
 * Global cache instance - will be managed by a React context or hook
 */
let globalFinancialCache: FinancialDataCache | null = null;

/**
 * Gets or builds the financial data cache
 */
export function getOrBuildCache(data: ChartData): FinancialDataCache {
  if (!isCacheValid(globalFinancialCache, data)) {
    console.log('Building new financial data cache...');
    globalFinancialCache = buildFinancialDataCache(data);
    console.log(`Cache built with ${globalFinancialCache.monthlyData.size} months of data`);
  }
  
  return globalFinancialCache!;
}

/**
 * Clears the global cache (useful for testing or force refresh)
 */
export function clearCache(): void {
  globalFinancialCache = null;
}
